/**
 * `/` — GAMES NIGHT HOME (FIRST NIGHT S1). Copy VERBATIM from the brief.
 * Skeleton→content via SkeletonToContent (KICKOFF §3.3 — loading is the
 * entrance). No real night config exists yet: the loader is an honest stub
 * seam (Loop 1/2 wires tonight's config); the hero slot self-announces per
 * KIT CHECK Rule 1.
 *
 * The volt CTA is the screen's ONE attractor (single attractor law).
 */
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { SkeletonToContent, Skeleton } from "@matchday/ui";
import { BreathingAttractor } from "@matchday/juice";
import { CodeBoxes } from "../components/CodeBoxes";
import { auth } from "../lib/platform";
import { nightCodeStorage } from "../lib/matchApi";
import { onboardingApi } from "../lib/onboardingApi";
import { landingDestination } from "../lib/onboardingFlow";

/** LOOP 1 SEAM: replace with the real night-config fetch (nights table). */
function loadNightConfig(): Promise<null> {
  return Promise.resolve(null);
}

export default function Landing() {
  const [loading, setLoading] = useState(true);
  const [nightCode, setNightCode] = useState<string | null>(null);
  const [, navigate] = useLocation();

  /**
   * CTA: no session → /auth (Auth.tsx runs this SAME onboarding check after
   * OTP succeeds — see its header comment). A session already on file routes
   * straight to onboarding-or-feed — NEVER back through this screen again.
   * WHISTLE CHECK §1/#2 (24 Jul real-device P0): the old version always sent
   * an authenticated player to /feed with no onboarding check (bug #3's other
   * half) and Auth.tsx used to bounce back to "/" post-OTP, re-showing this
   * exact code screen even though the code was already stored — which read as
   * "the code got asked for again." Both are gone now: this is the ONLY
   * place the code is entered, and every path out of auth lands on the right
   * screen directly.
   */
  const enter = async () => {
    if (nightCode !== null) nightCodeStorage.set(`BLG-${nightCode}`);
    const session = await auth.getSession();
    if (session === null) {
      navigate("/auth");
      return;
    }
    try {
      const { state } = await onboardingApi.getState();
      navigate(landingDestination(state));
    } catch {
      navigate("/feed"); // onboarding service down ≠ dead end — the same convention as HostWizard's create-time check
    }
  };

  useEffect(() => {
    let alive = true;
    void loadNightConfig().then(() => {
      if (alive) setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <SkeletonToContent
      loading={loading}
      skeleton={
        <div className="pl-landing">
          <Skeleton variant="panel" clip="notch-12" aspect={394 / 220} />
          <Skeleton variant="text-line" width="70%" />
          <Skeleton variant="text-line" width="90%" />
          <Skeleton variant="card" height={96} />
        </div>
      }
    >
      <main className="pl-landing">
        {/* The A1 Biggie hero render was never delivered — nothing in the pack
            matches it and the id appears in no manifest. It rendered as a
            dashed box captioned "BIGGIE HERO RENDER · 394×220 / AWAITING:
            A1_BIGGIE_HERO": the first thing a new player saw was the build's
            own to-do list. The headline below carries the screen perfectly
            well; the band comes back when there is art to put in it. */}

        <p className="pl-eyebrow">GAMES NIGHT · LIVE</p>
        <h1 className="pl-h1">WELCOME TO GAMES NIGHT</h1>
        <p className="pl-sub">EA FC fans from across the country. One arena. Every night.</p>
        <p className="pl-line2">
          Challenge your host BIGGIE. Play, win, claim prizes — just for pulling up.
        </p>
        <p className="pl-tag">Your nightly gaming fix starts now.</p>

        <div className="pl-codefield">
          <label className="pl-codefield-label">ENTER CODE TO JOIN</label>
          <CodeBoxes
            length={4}
            charset="alnum"
            prefix="BLG-"
            label="Night code"
            onComplete={setNightCode}
          />
        </div>

        <BreathingAttractor name="landing-cta" className="pl-cta-slot">
          {/* LOOP 1 SEAM: code validation ceremony (MATCH POINT §7) still
              pending — the CTA now routes by session: stored session → the
              feed; none → OTP. The entered code is stashed for the wizard. */}
          <button type="button" className="pl-cta" onClick={() => void enter()}>
            JOIN GAMES NIGHT
          </button>
        </BreathingAttractor>
      </main>
    </SkeletonToContent>
  );
}
